import { R } from "@mobily/ts-belt";
import {
  AccessionNumberFormat,
  ManualAccessionNumberError,
} from "generated/graphql";
import { match } from "ts-pattern";
import * as z from "zod";
import { AccessionDataSchemaZod } from "../accession-data-form/schema";
import { wildCollectionDataSchema } from "../accession-data-form/wild-collection-dialog";
import {
  MaterialSchema,
  PlantMaterialSchemaZod,
} from "../add-plant-material-form/schema";
import { makeValidationReq } from "../validate-accession-number/make-validation-req";

export const accessionNumberErrorMessages: Record<
  ManualAccessionNumberError,
  string
> = {
  [ManualAccessionNumberError.DuplicateAccessionNumber]:
    "Accession number already exists",
  [ManualAccessionNumberError.InvalidAccessionNumber]:
    "Manual accession numbers cannot be the same format as generated accession numbers",
  [ManualAccessionNumberError.InvalidCharacters]:
    "Accession number contains invalid characters",
  [ManualAccessionNumberError.InvalidInputs]:
    "Invalid accession number inputs, check all fields and try again",
};

export type AccessionNumberValidationFields = {
  accessionNumberFormat?: AccessionNumberFormat | null;
  accessionNumberSuffix?: number | null;
  exogenousAccessionNumber?: string | null;
  accessionDate: Date;
};

export const validateAccessionInput = async <
  T extends AccessionNumberValidationFields,
>(
  updates: T,
  ctx: z.RefinementCtx,
) => {
  await match(updates.accessionNumberFormat)
    .with(AccessionNumberFormat.PrefixSuffix, async () => {
      const path = ["accessionNumberSuffix"];
      if (updates.accessionNumberSuffix == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          message:
            "A valid Suffix is required when using the standard accession number format",
          path,
          expected: "string",
          received: "null",
        });
        return;
      }
      const accessionNumberPrefix = updates.accessionDate.getFullYear();
      const { accessionNumberSuffix } = updates;

      R.match(
        await makeValidationReq({
          accessionNumberPrefix,
          accessionNumberSuffix,
        }),
        (errors) => {
          errors.forEach((e) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: accessionNumberErrorMessages[e],
              path,
            });
          });
        },
        () => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Unable to validate accession number",
            path,
          });
        },
      );
    })
    .with(AccessionNumberFormat.Exogenous, async () => {
      const path = ["exogenousAccessionNumber"];
      const { exogenousAccessionNumber } = updates;

      if (exogenousAccessionNumber == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          message:
            "A valid string must be provided when using the custom accession number format",
          path,
          expected: "string",
          received: "null",
        });
        return;
      }
      if (exogenousAccessionNumber.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          message: "Custom accession number must be at least 3 digits long",
          path,
          expected: "string",
          received: "null",
        });
        return;
      }

      R.match(
        await makeValidationReq({
          exogenousAccessionNumber,
        }),
        (errors) => {
          errors.forEach((e) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: accessionNumberErrorMessages[e],
              path,
            });
          });
        },
        () => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Unable to validate accession number",
            path,
          });
        },
      );
    })
    .with(undefined, () => null)
    .with(null, () => null)
    .exhaustive();
};
export const createAccessionFormSchemaOld = AccessionDataSchemaZod.merge(
  wildCollectionDataSchema.merge(
    z.object({ plantMaterials: z.array(PlantMaterialSchemaZod) }),
  ),
).superRefine(validateAccessionInput);

export const createAccessionFormSchema = AccessionDataSchemaZod.merge(
  wildCollectionDataSchema.merge(
    z.object({ plantMaterials: z.array(MaterialSchema) }),
  ),
).superRefine(validateAccessionInput);

export type CreateAccessionFormSchemaOld = z.TypeOf<
  typeof createAccessionFormSchemaOld
>;
export type CreateAccessionFormSchemaInputOld = z.input<
  typeof createAccessionFormSchemaOld
>;

export type CreateAccessionFormSchema = z.TypeOf<
  typeof createAccessionFormSchema
>;
export type CreateAccessionFormSchemaInput = z.input<
  typeof createAccessionFormSchema
>;

export type SubsetOrNever<T extends string> =
  T extends keyof CreateAccessionFormSchema ? T : never;
